/**
 * Challenges list page. Renders the tab-scoped list of challenges, admin
 * import/export/sync/rollover toolbar, and hosts the Create Challenge
 * modal.
 *
 * The detail page (`ChallengeDetailPage`) and the create modal were
 * extracted into their own files in the component-splitting sprint —
 * this file used to be 1100+ lines.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Loader2, Trophy, Calendar, Plus, Upload, Download, RefreshCw,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

import { ChallengeCard } from '../components/social';
import { CreateChallengeModal } from '../components/social/CreateChallengeModal';
import { BulkImportChallengesModal } from '../components/BulkImportChallengesModal';
import { downloadJson } from '../utils/downloadJson';

const EMPTY_DRAFT = {
  title: '',
  description: '',
  type: 'workout_count',
  target: 10,
  duration_days: 7,
  is_team_challenge: false,
};

export const ChallengesPage = () => {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // all | my | active | completed
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const isAdmin = user?.is_admin === true;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allRes, myRes] = await Promise.all([
        api().get('/challenges'),
        api().get('/challenges/my-challenges'),
      ]);
      setChallenges(allRes.data.challenges || []);
      setMyChallenges(myRes.data.challenges || []);
    } catch {
      toast.error('Failed to load challenges');
    }
    setLoading(false);
  };

  const handleCreateChallenge = async () => {
    if (!draft.title.trim()) {
      toast.error('Please enter a challenge title');
      return;
    }
    setCreating(true);
    try {
      await api().post('/challenges', {
        title: draft.title,
        description: draft.description || 'Complete this fitness challenge!',
        challenge_type: draft.type,
        target_value: parseInt(draft.target, 10),
        duration_days: parseInt(draft.duration_days, 10),
        is_team_challenge: draft.is_team_challenge,
        is_public: true,
      });
      toast.success('Challenge created successfully!');
      setShowCreateModal(false);
      setDraft(EMPTY_DRAFT);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create challenge');
    }
    setCreating(false);
  };

  const getFilteredChallenges = () => {
    const now = new Date();
    switch (activeTab) {
      case 'my':
        return myChallenges;
      case 'active':
        return challenges.filter((c) => new Date(c.start_date) <= now && new Date(c.end_date) >= now);
      case 'completed':
        return challenges.filter((c) => new Date(c.end_date) < now);
      default:
        return challenges;
    }
  };

  // ---- Admin toolbar actions ----

  const handleExport = async () => {
    try {
      const res = await api().get('/admin/challenges/export');
      const items = res.data.challenges || [];
      if (!items.length) {
        toast.error('No challenges to export');
        return;
      }
      downloadJson(items, 'challenges-backup');
      toast.success(`Exported ${items.length} challenges`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };

  const handleSyncDailyTargets = async () => {
    try {
      const res = await api().post('/admin/challenges/sync-daily-targets');
      const d = res.data || {};
      toast.success(
        d.synced_count > 0
          ? `Synced daily targets on ${d.synced_count} challenge${d.synced_count === 1 ? '' : 's'}`
          : 'All daily targets already in sync'
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    }
  };

  const handleRollover = async () => {
    if (!window.confirm('Roll every challenge into the new cycle? Participant current progress resets to 0 but lifetime totals are preserved.')) return;
    try {
      const res = await api().post('/admin/challenges/rollover');
      const d = res.data || {};
      toast.success(`Rolled over ${d.rolled || 0} · reset ${d.participants_reset || 0} participants`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollover failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-primary animate-spin" />
      </div>
    );
  }

  const tabs = [
    { key: 'all', label: 'All Challenges' },
    { key: 'my', label: `My Challenges (${myChallenges.length})` },
    { key: 'active', label: 'Active Now' },
    { key: 'completed', label: 'Completed' },
  ];

  const filtered = getFilteredChallenges();

  return (
    <div className="min-h-screen bg-black" data-testid="challenges-page">
      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        >
          <div>
            <h2 className="font-heading text-3xl text-white uppercase tracking-wider mb-2">
              <Trophy className="h-8 w-8 text-yellow-400 inline mr-3" />
              Fitness <span className="text-primary">Challenges</span>
            </h2>
            <p className="text-muted-foreground">Compete with others and push your limits</p>
          </div>

          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setShowImportModal(true)}
                variant="outline"
                className="border-border text-white hover:bg-white/5"
                data-testid="import-challenges-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                className="border-border text-white hover:bg-white/5"
                data-testid="export-challenges-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button
                onClick={handleSyncDailyTargets}
                variant="outline"
                className="border-border text-white hover:bg-white/5"
                data-testid="sync-daily-targets-btn"
                title="Recompute daily_target from target_value / duration_days on every challenge"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync Targets
              </Button>
              <Button
                onClick={handleRollover}
                variant="outline"
                className="border-border text-white hover:bg-white/5"
                data-testid="rollover-challenges-btn"
                title="Snap every challenge to the current calendar cycle and reset current-cycle progress (preserves lifetime)"
              >
                <Calendar className="h-4 w-4 mr-2" />
                Roll Over
              </Button>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary text-black hover:bg-primary/90"
                data-testid="create-challenge-btn"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Challenge
              </Button>
            </div>
          )}
        </motion.div>

        <CreateChallengeModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          value={draft}
          onChange={setDraft}
          onSubmit={handleCreateChallenge}
          creating={creating}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-2 mb-8 flex-wrap"
        >
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.key)}
              className={activeTab === tab.key ? 'bg-primary text-black' : ''}
            >
              {tab.label}
            </Button>
          ))}
        </motion.div>

        {filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-card border border-border p-12 text-center"
          >
            <Trophy className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">
              No Challenges Found
            </h3>
            <p className="text-muted-foreground">
              {activeTab === 'my'
                ? "You haven't joined any challenges yet"
                : 'Check back later for new challenges'}
            </p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((challenge) => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                api={api}
                onJoin={fetchData}
                onLeave={fetchData}
                isAdmin={isAdmin}
                onChanged={fetchData}
              />
            ))}
          </div>
        )}
      </main>

      <BulkImportChallengesModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        api={api}
        onImportComplete={fetchData}
      />
    </div>
  );
};

export default ChallengesPage;
